'use client'

import { type ComponentType, useEffect, useState } from 'react'

/** The cookie Vercel Toolbar writes when someone overrides a flag. */
const OVERRIDE_COOKIE = 'vercel-flag-overrides='

/**
 * Cookie-free embeds framed on a customer's site: the proxy answers them
 * before its locale step, so a language choice there would change nothing.
 */
const EMBED_PATH_PREFIX = '/embed/'

/**
 * The floating language switcher, behind the `locale-switcher` flag
 * (`lib/flags/locale-switcher.ts`). Renders nothing for a visitor without a
 * Toolbar override: only then does it ask the server, and only when the
 * answer is on does the switcher's own chunk load. Pages stay static.
 * Never on an embed, where a choice could not take effect.
 */
export function LocaleSwitcherFlag() {
  const [Switcher, setSwitcher] = useState<ComponentType | null>(null)
  useEffect(() => {
    if (
      window.location.pathname.startsWith(EMBED_PATH_PREFIX) ||
      !document.cookie.includes(OVERRIDE_COOKIE)
    ) {
      return
    }
    let isCurrent = true
    fetch('/api/flags/locale-switcher', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then(async (body: { enabled?: unknown } | null) => {
        if (!isCurrent || body?.enabled !== true) {
          return
        }
        const { FloatingLocaleSwitcher } = await import(
          './floating-locale-switcher'
        )
        if (isCurrent) {
          setSwitcher(() => FloatingLocaleSwitcher)
        }
      })
      .catch(() => {
        // A testing aid: when the check fails, the page simply has no switcher.
      })
    return () => {
      isCurrent = false
    }
  }, [])
  return Switcher ? <Switcher /> : null
}
